import api from './api';
import { API_BASE_URL } from '../config/environment';

/**
 * Authentication Service
 * Handles all authentication-related operations with the backend
 */
class AuthService {
  /**
   * Initiate Google OAuth login
   * Redirects user to backend's OAuth endpoint
   */
  loginWithGoogle() {
    window.location.href = `${API_BASE_URL}/auth/login/google`;
  }

  /**
   * Get current authenticated user
   * @returns {Promise<Object>} User info
   */
  async getCurrentUser() {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      console.error('Error getting current user:', error);
      throw error;
    }
  }

  /**
   * Logout user
   * Clears token from localStorage and calls backend logout
   */
  async logout() {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      // Always clear token even if backend call fails
      this.clearToken();
    }
  }

  /**
   * Store authentication token
   * @param {string} token - JWT token from backend
   */
  setToken(token) {
    if (token) {
      localStorage.setItem('auth_token', token);
    }
  }

  /**
   * Get stored authentication token
   * @returns {string|null} Token or null if not found
   */
  getToken() {
    return localStorage.getItem('auth_token');
  }

  /**
   * Clear authentication token
   */
  clearToken() {
    localStorage.removeItem('auth_token');
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} True if token exists
   */
  isAuthenticated() {
    return !!this.getToken();
  }

  /**
   * Verify token validity by calling backend
   * @returns {Promise<boolean>} True if token is valid
   */
  async verifyToken() {
    if (!this.isAuthenticated()) {
      return false;
    }

    try {
      await this.getCurrentUser();
      return true;
    } catch (error) {
      // Token is invalid, clear it
      this.clearToken();
      return false;
    }
  }

  /**
   * Sign up with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} Auth response with token and user
   */
  async signUpWithEmail(email, password) {
    try {
      const response = await api.post('/auth/signup/email', {
        email,
        password
      });

      if (response.data.token) {
        this.setToken(response.data.token);
      }

      return response.data;
    } catch (error) {
      console.error('Error signing up with email:', error);
      throw error;
    }
  }

  /**
   * Login with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} Auth response with token and user
   */
  async loginWithEmail(email, password) {
    try {
      const response = await api.post('/auth/login/email', {
        email,
        password
      });

      if (response.data.token) {
        this.setToken(response.data.token);
      }

      return response.data;
    } catch (error) {
      console.error('Error logging in with email:', error);
      throw error;
    }
  }
}

// Export singleton instance
const authService = new AuthService();
export default authService;
